import React from 'react';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Instagram, Printer } from 'lucide-react';

const Footer = () => {
  return (
    <footer id="contact" className="bg-gray-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* About */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png" 
              alt="SDORA Wellbeing" 
              className="h-28 w-auto mb-4"
              loading="lazy"
            />
            <p className="text-gray-400 mb-4">
              Licensed and accredited behavioral health organization providing compassionate, evidence-based care.
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/p/SDORA-Wellbeing-LLC-100086663974006/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#1877F2] transition-colors" aria-label="SDORA Wellbeing on Facebook" data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/sdorawellbeing/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#E4405F] transition-colors" aria-label="SDORA Wellbeing on Instagram" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="/services/comprehensive-mental-health" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-services">Services</a></li>
              <li><a href="/team" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-our-team">Our Team</a></li>
              <li><a href="/testimonials" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-testimonials">Testimonials</a></li>
              <li><a href="/resources" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-resources">Resources</a></li>
              <li><a href="/insurance" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-insurance">Insurance</a></li>
              <li><a href="/faq" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-faq">FAQ</a></li>
              <li><a href="/get-started" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-get-started">Get Started</a></li>
              <li><a href="/careers" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-link-careers">Careers</a></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li><a href="/services/outpatient-mental-health" className="text-gray-400 hover:text-white transition-colors">Individual Therapy</a></li>
              <li><a href="/services/outpatient-mental-health" className="text-gray-400 hover:text-white transition-colors">Family Therapy</a></li>
              <li><a href="/services/medication-management" className="text-gray-400 hover:text-white transition-colors">Medication Management</a></li>
              <li><a href="/services/psychiatric-rehabilitation" className="text-gray-400 hover:text-white transition-colors">Psychiatric Rehabilitation</a></li>
              <li><a href="/services/case-management" className="text-gray-400 hover:text-white transition-colors">Case Management</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400">1700 Reisterstown RD STE 109<br />Pikesville, MD 21208</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a href="tel:+18777725150" className="text-gray-400 hover:text-white transition-colors">
                  (877) 772-5150
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-400">Fax: (667) 400-5108</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a href="mailto:info@sdorawellbeing.org" className="text-gray-400 hover:text-white transition-colors">
                  info@sdorawellbeing.org
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          {/* Regulatory Logos — including veterans badge */}
          <h3 id="accreditation" className="text-center text-sm font-semibold text-gray-400 tracking-wide uppercase mb-6 scroll-mt-24">
            Licensure, Accreditation &amp; Affiliations
          </h3>
          <div className="flex justify-center items-center gap-8 mb-8 flex-wrap" data-testid="badge-row">
            <div className="bg-white rounded-lg p-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/c0a5v1h1_mrd-768x189.png" 
                alt="Maryland Department of Health" 
                className="h-16 w-auto"
                loading="lazy"
              />
            </div>
            <div className="bg-white rounded-lg p-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/jwyrvdww_carf_logo-500x500.png" 
                alt="CARF Accredited" 
                className="h-16 w-auto"
                loading="lazy"
              />
            </div>
            <div className="bg-white rounded-lg p-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_01501ca6-ddde-43b6-bfc5-0444b2f0bae0/artifacts/vj4vchgs_proudlycaringforveterans-horizbadge-300x100.png" 
                alt="Proudly Caring for Veterans" 
                className="h-16 w-auto"
                data-testid="veterans-badge"
                loading="lazy"
              />
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} SDORA Wellbeing LLC. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;